import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { localize } from 'react-localize-redux';
import { change, submit, reset } from 'redux-form';

import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import FlatButton from '@material-ui/core/Button';
import withMobileDialog from '@material-ui/core/withMobileDialog';

import ButtonSwitchNavigation from 'components/shared/ButtonSwitchNavigation';
import { AccentColor } from 'helpers/AccentColorHelper';
import { colors } from 'helpers/ColorHelper';
import { featureEnabled } from 'helpers/ToggleBot';
import NoticeForm from './NoticeForm';
import NoticeTemplateForm from './NoticeTemplateForm';
import { RenderIfToggleOff, RenderIfToggleOn } from 'components/shared/ToggleRenderer';

const styles = {
  icon: {
    width: '28px',
  },
};

// value intentially chosen as the index of this option in ButtonSwitchNavigation
const NOTICE = 0;
const TEMPLATE = 1;

const ADD_NOTICE_FORM = 'add_notice_form';
const TEMPLATE_FORM = 'template_form';

class AddNoticeDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeForm: NOTICE,
      templateFormMode: null,
    };
  }

  handleChange = (selectedIndex) => {
    this.setState({
      activeForm: selectedIndex,
    });
  };

  handleSubmit = () => {
    // If new notices drawer, we're only using this dialog for template management.
    if (featureEnabled('ui.notices.new_drawer')) {
      this.props.submit(TEMPLATE_FORM);
      return;
    }

    if (this.state.activeForm === NOTICE) {
      this.props.submit(ADD_NOTICE_FORM);
    } else if (this.state.activeForm === TEMPLATE) {
      this.props.submit(TEMPLATE_FORM);
    }
  };

  changeTemplateFormMode = (toMode) => {
    this.setState({
      templateFormMode: toMode,
    });
  };

  handleSubmitTemplate = (formData) => {
    return this.props.submitTemplateForm(formData).then(() => {
      this.props.reset(TEMPLATE_FORM);
      this.changeTemplateFormMode(null);
    });
  };

  deleteTemplate = (noticeTemplateId) => {
    return this.props.deleteTemplate(noticeTemplateId).then(() => {
      this.props.reset(TEMPLATE_FORM);
      this.changeTemplateFormMode(null);
    });
  };

  closeDialog = () => {
    this.setState({
      activeForm: NOTICE,
      templateFormMode: null,
    });
    this.props.toggleDialog();
  };

  render() {
    const { translate } = this.props;
    const shouldShowSaveButton =
      this.state.activeForm === NOTICE || this.state.templateFormMode !== null;

    const dialogTitle = featureEnabled('ui.notices.new_drawer')
      ? 'landlordPortal.addNoticeDialog.manageTemplates'
      : 'landlordPortal.addNoticeDialog.addNewNotice';
    return (
      <Dialog
        disableBackdropClick
        fullScreen={this.props.fullScreen}
        open={this.props.open}
        onClose={this.closeDialog}
        aria-labelledby="add-new-notice"
        classes={{ paper: 'form-dialog' }}
      >
        <DialogTitle id="add-new-notice">
          <h3 style={{ textAlign: 'center', margin: 0 }}>{translate(dialogTitle)}</h3>
        </DialogTitle>
        <DialogContent>{this.renderDialogContent()}</DialogContent>
        <DialogActions style={{ display: 'block' }}>
          <FlatButton onClick={this.closeDialog} id="close-button" style={{ color: AccentColor() }}>
            {translate('landlordPortal.addNoticeDialog.close')}
          </FlatButton>
          {shouldShowSaveButton && (
            <FlatButton
              variant="raised"
              onClick={this.handleSubmit}
              disabled={this.props.disabledAddNoticeSubmit}
              id="submit-button"
              style={{
                color: 'white',
                backgroundColor: this.props.disabledAddNoticeSubmit
                  ? colors.LIGHT_GRAY
                  : AccentColor(),
              }}
            >
              {translate('landlordPortal.addNoticeDialog.save')}
            </FlatButton>
          )}
        </DialogActions>
      </Dialog>
    );
  }

  renderNoticeIcon = () => {
    return <img style={styles.icon} src="/notice.svg" alt="New Notice" />;
  };

  renderTemplateIcon = () => {
    return <img style={styles.icon} src="/template.svg" alt="New Template" />;
  };

  renderNoticeForm = () => {
    if (this.state.activeForm !== NOTICE) return null;
    return (
      <NoticeForm
        properties={this.props.properties}
        noticeTemplates={this.props.noticeTemplates}
        formName={ADD_NOTICE_FORM}
        onSubmit={this.props.postNotice}
      />
    );
  };

  renderTemplateForm = () => {
    if (this.state.activeForm !== TEMPLATE) return null;
    return (
      <NoticeTemplateForm
        mode={this.state.templateFormMode}
        noticeTemplates={this.props.noticeTemplates}
        deleteTemplate={this.deleteTemplate}
        formName={TEMPLATE_FORM}
        onSubmit={this.handleSubmitTemplate}
        changeTemplateFormMode={this.changeTemplateFormMode}
      />
    );
  };

  renderDialogContent = () => {
    if (!this.props.noticeTemplateManager) return this.renderNoticeForm();

    // If the new drawer is enabled, render only the template form in the old dialog
    // TODO replace with new NoticeTemplateForm drawer
    return (
      <>
        <RenderIfToggleOn toggle={'ui.notices.new_drawer'}>
          <NoticeTemplateForm
            mode={this.state.templateFormMode}
            noticeTemplates={this.props.noticeTemplates}
            deleteTemplate={this.deleteTemplate}
            formName={TEMPLATE_FORM}
            onSubmit={this.handleSubmitTemplate}
            changeTemplateFormMode={this.changeTemplateFormMode}
          />
        </RenderIfToggleOn>
        <RenderIfToggleOff toggle={'ui.notices.new_drawer'}>
          <Fragment>
            <ButtonSwitchNavigation
              buttonLabels={[
                this.props.translate('landlordPortal.addNoticeDialog.createNewNotice').props
                  .children,
                this.props.translate('landlordPortal.addNoticeDialog.manageTemplates').props
                  .children,
              ]}
              buttonIcons={[this.renderNoticeIcon(), this.renderTemplateIcon()]}
              handleChange={this.handleChange}
            />
            {this.renderNoticeForm()}
            {this.renderTemplateForm()}
          </Fragment>
        </RenderIfToggleOff>
      </>
    );
  };
}

let ConnectedAddNoticeDialog = connect(
  (store) => ({
    noticeTemplateManager: store.userTypes.noticeTemplateManager,
  }),
  {
    change,
    submit,
    reset,
  }
)(AddNoticeDialog);

export default withMobileDialog()(localize()(ConnectedAddNoticeDialog));
